'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';

/* ------------------------------------------------------------------ */
/*  Salary data: realistic 2024-2025 US base salary ranges (annual)   */
/*  p10 / p25 / p50 / p75 / p90 for mid-level (3-5 yrs experience)   */
/*  in a mid-size-city / national-average market                       */
/* ------------------------------------------------------------------ */

interface RoleSalary {
  label: string;
  category: string;
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  bonusPct: number;     // typical bonus as % of base
  equityPct: number;    // typical equity/RSU as % of base (annualized)
  benefitsValue: number; // approximate annual benefits value in dollars
}

const ROLES: Record<string, RoleSalary> = {
  software_engineer:         { label: 'Software Engineer',              category: 'Engineering',   p10: 75000,  p25: 95000,  p50: 120000, p75: 155000, p90: 190000, bonusPct: 10, equityPct: 15,  benefitsValue: 18000 },
  senior_software_engineer:  { label: 'Senior Software Engineer',       category: 'Engineering',   p10: 110000, p25: 130000, p50: 160000, p75: 195000, p90: 235000, bonusPct: 12, equityPct: 20,  benefitsValue: 22000 },
  staff_engineer:            { label: 'Staff / Principal Engineer',     category: 'Engineering',   p10: 150000, p25: 175000, p50: 210000, p75: 260000, p90: 320000, bonusPct: 15, equityPct: 30,  benefitsValue: 25000 },
  frontend_developer:        { label: 'Frontend Developer',             category: 'Engineering',   p10: 65000,  p25: 85000,  p50: 110000, p75: 140000, p90: 170000, bonusPct: 8,  equityPct: 10,  benefitsValue: 16000 },
  backend_developer:         { label: 'Backend Developer',              category: 'Engineering',   p10: 70000,  p25: 90000,  p50: 115000, p75: 150000, p90: 185000, bonusPct: 9,  equityPct: 12,  benefitsValue: 17000 },
  fullstack_developer:       { label: 'Full-Stack Developer',           category: 'Engineering',   p10: 68000,  p25: 90000,  p50: 118000, p75: 150000, p90: 185000, bonusPct: 9,  equityPct: 12,  benefitsValue: 17000 },
  mobile_developer:          { label: 'Mobile Developer (iOS/Android)', category: 'Engineering',   p10: 70000,  p25: 92000,  p50: 120000, p75: 155000, p90: 190000, bonusPct: 10, equityPct: 14,  benefitsValue: 17000 },
  devops_engineer:           { label: 'DevOps / SRE Engineer',          category: 'Engineering',   p10: 80000,  p25: 100000, p50: 130000, p75: 165000, p90: 200000, bonusPct: 10, equityPct: 15,  benefitsValue: 18000 },
  cloud_architect:           { label: 'Cloud / Solutions Architect',    category: 'Engineering',   p10: 110000, p25: 130000, p50: 160000, p75: 195000, p90: 235000, bonusPct: 12, equityPct: 18,  benefitsValue: 22000 },
  cybersecurity_analyst:     { label: 'Cybersecurity Analyst',          category: 'Engineering',   p10: 68000,  p25: 85000,  p50: 110000, p75: 140000, p90: 170000, bonusPct: 8,  equityPct: 8,   benefitsValue: 17000 },
  qa_engineer:               { label: 'QA / Test Engineer',             category: 'Engineering',   p10: 55000,  p25: 70000,  p50: 90000,  p75: 115000, p90: 140000, bonusPct: 7,  equityPct: 8,   benefitsValue: 15000 },
  data_scientist:            { label: 'Data Scientist',                 category: 'Data & AI',     p10: 78000,  p25: 95000,  p50: 125000, p75: 160000, p90: 195000, bonusPct: 10, equityPct: 15,  benefitsValue: 18000 },
  data_analyst:              { label: 'Data Analyst',                   category: 'Data & AI',     p10: 48000,  p25: 60000,  p50: 78000,  p75: 95000,  p90: 115000, bonusPct: 6,  equityPct: 5,   benefitsValue: 14000 },
  data_engineer:             { label: 'Data Engineer',                  category: 'Data & AI',     p10: 82000,  p25: 100000, p50: 130000, p75: 165000, p90: 200000, bonusPct: 10, equityPct: 14,  benefitsValue: 18000 },
  ml_engineer:               { label: 'Machine Learning Engineer',      category: 'Data & AI',     p10: 95000,  p25: 115000, p50: 145000, p75: 185000, p90: 225000, bonusPct: 12, equityPct: 20,  benefitsValue: 20000 },
  ai_researcher:             { label: 'AI / Research Scientist',        category: 'Data & AI',     p10: 110000, p25: 135000, p50: 175000, p75: 225000, p90: 300000, bonusPct: 15, equityPct: 25,  benefitsValue: 22000 },
  product_manager:           { label: 'Product Manager',                category: 'Product & PM',  p10: 82000,  p25: 100000, p50: 130000, p75: 165000, p90: 200000, bonusPct: 12, equityPct: 18,  benefitsValue: 19000 },
  senior_product_manager:    { label: 'Senior Product Manager',         category: 'Product & PM',  p10: 120000, p25: 140000, p50: 170000, p75: 205000, p90: 245000, bonusPct: 15, equityPct: 25,  benefitsValue: 22000 },
  project_manager:           { label: 'Project Manager',                category: 'Product & PM',  p10: 58000,  p25: 75000,  p50: 95000,  p75: 120000, p90: 145000, bonusPct: 8,  equityPct: 5,   benefitsValue: 16000 },
  scrum_master:              { label: 'Scrum Master / Agile Coach',     category: 'Product & PM',  p10: 72000,  p25: 90000,  p50: 115000, p75: 140000, p90: 165000, bonusPct: 8,  equityPct: 8,   benefitsValue: 17000 },
  ux_designer:               { label: 'UX Designer',                    category: 'Design',        p10: 62000,  p25: 80000,  p50: 105000, p75: 130000, p90: 160000, bonusPct: 7,  equityPct: 10,  benefitsValue: 16000 },
  ui_designer:               { label: 'UI Designer',                    category: 'Design',        p10: 55000,  p25: 72000,  p50: 92000,  p75: 115000, p90: 140000, bonusPct: 6,  equityPct: 8,   benefitsValue: 15000 },
  product_designer:          { label: 'Product Designer',               category: 'Design',        p10: 72000,  p25: 90000,  p50: 118000, p75: 150000, p90: 185000, bonusPct: 8,  equityPct: 14,  benefitsValue: 17000 },
  graphic_designer:          { label: 'Graphic Designer',               category: 'Design',        p10: 38000,  p25: 48000,  p50: 60000,  p75: 75000,  p90: 92000,  bonusPct: 4,  equityPct: 2,   benefitsValue: 12000 },
  marketing_manager:         { label: 'Marketing Manager',              category: 'Marketing',     p10: 55000,  p25: 72000,  p50: 95000,  p75: 125000, p90: 155000, bonusPct: 10, equityPct: 8,   benefitsValue: 16000 },
  digital_marketing:         { label: 'Digital Marketing Specialist',   category: 'Marketing',     p10: 40000,  p25: 52000,  p50: 68000,  p75: 88000,  p90: 110000, bonusPct: 6,  equityPct: 3,   benefitsValue: 13000 },
  content_strategist:        { label: 'Content Strategist',             category: 'Marketing',     p10: 45000,  p25: 58000,  p50: 75000,  p75: 95000,  p90: 115000, bonusPct: 6,  equityPct: 4,   benefitsValue: 14000 },
  seo_specialist:            { label: 'SEO Specialist',                 category: 'Marketing',     p10: 42000,  p25: 52000,  p50: 68000,  p75: 85000,  p90: 105000, bonusPct: 6,  equityPct: 3,   benefitsValue: 13000 },
  sales_representative:      { label: 'Sales Representative',           category: 'Sales',         p10: 38000,  p25: 50000,  p50: 65000,  p75: 85000,  p90: 110000, bonusPct: 25, equityPct: 3,   benefitsValue: 14000 },
  account_executive:         { label: 'Account Executive',              category: 'Sales',         p10: 50000,  p25: 65000,  p50: 85000,  p75: 115000, p90: 150000, bonusPct: 30, equityPct: 8,   benefitsValue: 16000 },
  sales_manager:             { label: 'Sales Manager',                  category: 'Sales',         p10: 72000,  p25: 90000,  p50: 115000, p75: 148000, p90: 185000, bonusPct: 25, equityPct: 10,  benefitsValue: 18000 },
  financial_analyst:         { label: 'Financial Analyst',              category: 'Finance',       p10: 52000,  p25: 65000,  p50: 82000,  p75: 100000, p90: 125000, bonusPct: 10, equityPct: 5,   benefitsValue: 16000 },
  accountant:                { label: 'Accountant',                     category: 'Finance',       p10: 45000,  p25: 55000,  p50: 70000,  p75: 88000,  p90: 108000, bonusPct: 6,  equityPct: 2,   benefitsValue: 14000 },
  controller:                { label: 'Controller / Finance Manager',   category: 'Finance',       p10: 85000,  p25: 105000, p50: 135000, p75: 170000, p90: 210000, bonusPct: 15, equityPct: 10,  benefitsValue: 20000 },
  hr_manager:                { label: 'HR Manager',                     category: 'HR',            p10: 58000,  p25: 75000,  p50: 95000,  p75: 120000, p90: 145000, bonusPct: 8,  equityPct: 5,   benefitsValue: 16000 },
  recruiter:                 { label: 'Recruiter',                      category: 'HR',            p10: 42000,  p25: 52000,  p50: 68000,  p75: 88000,  p90: 110000, bonusPct: 8,  equityPct: 4,   benefitsValue: 14000 },
  business_analyst:          { label: 'Business Analyst',               category: 'Operations',    p10: 52000,  p25: 68000,  p50: 85000,  p75: 108000, p90: 130000, bonusPct: 8,  equityPct: 5,   benefitsValue: 15000 },
  operations_manager:        { label: 'Operations Manager',             category: 'Operations',    p10: 55000,  p25: 70000,  p50: 90000,  p75: 115000, p90: 140000, bonusPct: 10, equityPct: 5,   benefitsValue: 16000 },
  customer_success:          { label: 'Customer Success Manager',       category: 'Operations',    p10: 48000,  p25: 60000,  p50: 78000,  p75: 100000, p90: 125000, bonusPct: 8,  equityPct: 5,   benefitsValue: 15000 },
  technical_writer:          { label: 'Technical Writer',               category: 'Operations',    p10: 50000,  p25: 62000,  p50: 78000,  p75: 98000,  p90: 120000, bonusPct: 5,  equityPct: 5,   benefitsValue: 14000 },
  registered_nurse:          { label: 'Registered Nurse',               category: 'Healthcare',    p10: 52000,  p25: 62000,  p50: 78000,  p75: 95000,  p90: 115000, bonusPct: 4,  equityPct: 0,   benefitsValue: 16000 },
  physician_assistant:       { label: 'Physician Assistant',            category: 'Healthcare',    p10: 90000,  p25: 105000, p50: 120000, p75: 138000, p90: 158000, bonusPct: 5,  equityPct: 0,   benefitsValue: 20000 },
  pharmacist:                { label: 'Pharmacist',                     category: 'Healthcare',    p10: 100000, p25: 115000, p50: 130000, p75: 145000, p90: 160000, bonusPct: 4,  equityPct: 0,   benefitsValue: 20000 },
  teacher:                   { label: 'Teacher (K-12)',                 category: 'Education',     p10: 35000,  p25: 42000,  p50: 55000,  p75: 68000,  p90: 82000,  bonusPct: 2,  equityPct: 0,   benefitsValue: 14000 },
  mechanical_engineer:       { label: 'Mechanical Engineer',            category: 'Engineering',   p10: 58000,  p25: 72000,  p50: 90000,  p75: 112000, p90: 135000, bonusPct: 7,  equityPct: 4,   benefitsValue: 16000 },
  electrical_engineer:       { label: 'Electrical Engineer',            category: 'Engineering',   p10: 60000,  p25: 75000,  p50: 95000,  p75: 118000, p90: 142000, bonusPct: 7,  equityPct: 4,   benefitsValue: 16000 },
  civil_engineer:            { label: 'Civil Engineer',                 category: 'Engineering',   p10: 55000,  p25: 68000,  p50: 85000,  p75: 105000, p90: 128000, bonusPct: 6,  equityPct: 2,   benefitsValue: 15000 },
};

/* ------------------------------------------------------------------ */
/*  Location & experience multipliers                                  */
/* ------------------------------------------------------------------ */

const LOCATIONS = [
  { value: 'major_metro', label: 'Major Metro (NYC, SF, LA, Chicago, Seattle, Boston)' },
  { value: 'mid_city',    label: 'Mid-Size City (Austin, Denver, Nashville, Raleigh)' },
  { value: 'small_city',  label: 'Small City / Rural Area' },
  { value: 'remote',      label: 'Remote (location-independent)' },
] as const;

const EXPERIENCE_LEVELS = [
  { value: 'entry',     label: 'Entry Level (0-2 years)',      multiplier: 0.78 },
  { value: 'mid',       label: 'Mid Level (3-5 years)',        multiplier: 1.00 },
  { value: 'senior',    label: 'Senior Level (6-10 years)',    multiplier: 1.18 },
  { value: 'executive', label: 'Executive / Lead (10+ years)', multiplier: 1.35 },
] as const;

function locationMultiplier(loc: string): number {
  switch (loc) {
    case 'major_metro': return 1.18;
    case 'mid_city':    return 1.00;
    case 'small_city':  return 0.87;
    case 'remote':      return 1.05;
    default:            return 1.00;
  }
}

function locationLabel(loc: string): string {
  return LOCATIONS.find(l => l.value === loc)?.label ?? loc;
}

function experienceMultiplier(exp: string): number {
  return EXPERIENCE_LEVELS.find(e => e.value === exp)?.multiplier ?? 1.0;
}

function experienceLabel(exp: string): string {
  return EXPERIENCE_LEVELS.find(e => e.value === exp)?.label ?? exp;
}

/* ------------------------------------------------------------------ */
/*  Related roles: find 3-4 roles in the same category                 */
/* ------------------------------------------------------------------ */

function getRelatedRoles(currentKey: string): { key: string; role: RoleSalary }[] {
  const current = ROLES[currentKey];
  if (!current) return [];
  return Object.entries(ROLES)
    .filter(([key, r]) => key !== currentKey && r.category === current.category)
    .sort((a, b) => Math.abs(a[1].p50 - current.p50) - Math.abs(b[1].p50 - current.p50))
    .slice(0, 4)
    .map(([key, role]) => ({ key, role }));
}

/* ------------------------------------------------------------------ */
/*  Result types                                                       */
/* ------------------------------------------------------------------ */

interface SalaryResult {
  role: RoleSalary;
  roleKey: string;
  location: string;
  experience: string;
  adjP10: number;
  adjP25: number;
  adjP50: number;
  adjP75: number;
  adjP90: number;
  totalCompMedian: number;
  bonusAmount: number;
  equityAmount: number;
  benefitsValue: number;
  experienceImpact: { level: string; median: number }[];
  relatedRoles: { key: string; role: RoleSalary; adjP50: number }[];
  negotiationTips: string[];
}

/* ------------------------------------------------------------------ */
/*  Core research function                                             */
/* ------------------------------------------------------------------ */

function researchSalary(roleKey: string, location: string, experience: string): SalaryResult {
  const role = ROLES[roleKey];
  const locM = locationMultiplier(location);
  const expM = experienceMultiplier(experience);

  const adj = (base: number) => Math.round(base * locM * expM);
  const adjP10 = adj(role.p10);
  const adjP25 = adj(role.p25);
  const adjP50 = adj(role.p50);
  const adjP75 = adj(role.p75);
  const adjP90 = adj(role.p90);

  const bonusAmount = Math.round(adjP50 * (role.bonusPct / 100));
  const equityAmount = Math.round(adjP50 * (role.equityPct / 100));
  const totalCompMedian = adjP50 + bonusAmount + equityAmount + role.benefitsValue;

  // Experience impact: show what each level earns for this role+location
  const experienceImpact = EXPERIENCE_LEVELS.map(e => ({
    level: e.label.split(' (')[0],
    median: Math.round(role.p50 * locM * e.multiplier),
  }));

  // Related roles with adjusted medians
  const relatedRoles = getRelatedRoles(roleKey).map(({ key, role: r }) => ({
    key,
    role: r,
    adjP50: Math.round(r.p50 * locM * expM),
  }));

  // Negotiation leverage tips
  const negotiationTips: string[] = [];

  if (locM >= 1.15) {
    negotiationTips.push('Major metro salaries are 15-20% higher than national averages. Use city-specific data when negotiating -- do not accept a generic national number.');
  }
  if (locM < 0.9) {
    negotiationTips.push('Smaller markets pay less, but if you can find a remote role with a major-metro employer, you may earn 10-20% more than your local rate.');
  }
  if (location === 'remote') {
    negotiationTips.push('Remote roles vary widely: some companies pay the same regardless of location, while others adjust by your local cost of living. Ask about their compensation philosophy before naming a number.');
  }

  if (experience === 'entry') {
    negotiationTips.push('As an entry-level candidate, focus on demonstrating your learning velocity, relevant projects, and certifications rather than years of experience.');
  }
  if (experience === 'senior' || experience === 'executive') {
    negotiationTips.push('At your experience level, negotiate beyond base salary: signing bonuses, equity refreshers, performance accelerators, and title bumps often add 20-40% to your total comp.');
  }

  if (role.equityPct >= 15) {
    negotiationTips.push(`This role commonly includes ${role.equityPct}%+ of base in equity/RSUs. Always ask about equity grants -- they can be worth more than a base salary increase over 4 years.`);
  }
  if (role.bonusPct >= 15) {
    negotiationTips.push(`Bonus targets of ${role.bonusPct}%+ are typical for this role. Clarify the bonus structure: is it guaranteed, performance-based, or discretionary?`);
  }

  negotiationTips.push(
    'Research 3+ salary data sources (Levels.fyi, Glassdoor, Payscale, salary.com) and present a range rather than a single number. Anchor at the 75th percentile.',
    'Timing matters: negotiate after receiving a written offer but before signing. Once you accept, your leverage drops to zero.',
    'If the company cannot meet your base salary ask, negotiate for a sign-on bonus, accelerated review cycle (6 months instead of 12), or additional PTO.',
  );

  return {
    role, roleKey, location, experience,
    adjP10, adjP25, adjP50, adjP75, adjP90,
    totalCompMedian, bonusAmount, equityAmount, benefitsValue: role.benefitsValue,
    experienceImpact, relatedRoles, negotiationTips,
  };
}

/* ------------------------------------------------------------------ */
/*  Formatting helpers                                                 */
/* ------------------------------------------------------------------ */

function fmt(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

/* ------------------------------------------------------------------ */
/*  Salary Range Visualization                                         */
/* ------------------------------------------------------------------ */

function SalaryRangeBar({
  p10, p25, p50, p75, p90,
}: {
  p10: number; p25: number; p50: number; p75: number; p90: number;
}) {
  const min = p10 * 0.85;
  const max = p90 * 1.15;
  const range = max - min;
  const pct = (v: number) => Math.max(0, Math.min(100, ((v - min) / range) * 100));

  const markers = [
    { label: '10th', value: p10, color: '#ef4444' },
    { label: '25th', value: p25, color: '#f59e0b' },
    { label: '50th', value: p50, color: '#16a34a' },
    { label: '75th', value: p75, color: '#2563eb' },
    { label: '90th', value: p90, color: '#7c3aed' },
  ];

  return (
    <div className="w-full py-4">
      {/* The bar */}
      <div className="relative">
        {/* Background track */}
        <div className="h-8 rounded-full overflow-hidden flex">
          <div className="h-full bg-red-100" style={{ width: `${pct(p10)}%` }} />
          <div className="h-full bg-amber-100" style={{ width: `${pct(p25) - pct(p10)}%` }} />
          <div className="h-full bg-green-200" style={{ width: `${pct(p50) - pct(p25)}%` }} />
          <div className="h-full bg-green-300" style={{ width: `${pct(p75) - pct(p50)}%` }} />
          <div className="h-full bg-blue-200" style={{ width: `${pct(p90) - pct(p75)}%` }} />
          <div className="h-full bg-violet-100" style={{ width: `${100 - pct(p90)}%` }} />
        </div>

        {/* Percentile markers */}
        {markers.map((m) => (
          <div key={m.label}>
            <div
              className="absolute top-0 h-8 w-0.5"
              style={{ left: `${pct(m.value)}%`, backgroundColor: m.color }}
            />
            <div
              className="absolute -bottom-14 text-center -translate-x-1/2"
              style={{ left: `${pct(m.value)}%` }}
            >
              <span className="text-[10px] font-bold block" style={{ color: m.color }}>{m.label}</span>
              <span className="text-xs font-semibold text-gray-700">{fmt(m.value)}</span>
            </div>
          </div>
        ))}

        {/* "Most likely" range highlight */}
        <div
          className="absolute top-0 h-8 border-2 border-green-600 rounded-lg pointer-events-none"
          style={{
            left: `${pct(p25)}%`,
            width: `${pct(p75) - pct(p25)}%`,
          }}
        />
      </div>

      {/* Spacer for labels */}
      <div className="h-12" />

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-2">
        <span className="flex items-center gap-1">
          <span className="inline-block w-4 h-1.5 rounded bg-green-600" />
          Most likely range (25th - 75th)
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function SalaryResearchContent() {
  const [roleKey, setRoleKey] = useState('software_engineer');
  const [location, setLocation] = useState('mid_city');
  const [experience, setExperience] = useState('mid');
  const [result, setResult] = useState<SalaryResult | null>(null);

  function handleResearch() {
    setResult(researchSalary(roleKey, location, experience));
  }

  // Group roles by category for the select
  const rolesByCategory: Record<string, { key: string; label: string }[]> = {};
  Object.entries(ROLES).forEach(([key, role]) => {
    if (!rolesByCategory[role.category]) rolesByCategory[role.category] = [];
    rolesByCategory[role.category].push({ key, label: role.label });
  });
  // Sort categories and roles within
  const sortedCategories = Object.keys(rolesByCategory).sort();
  sortedCategories.forEach(cat => {
    rolesByCategory[cat].sort((a, b) => a.label.localeCompare(b.label));
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      <ToolHero
        title="Salary Research Tool"
        description="Research salary ranges by job title and location. See base pay, total compensation, and how experience level affects earnings."
        category="Salary Tools"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* ---------- Input Form ---------- */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 text-lg font-extrabold">$</span>
            Research a Role
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            All data is calculated locally in your browser. We never collect or transmit your information.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Job Title */}
            <div className="md:col-span-3">
              <label htmlFor="role" className="block text-sm font-semibold text-gray-700 mb-2">
                Job Title
              </label>
              <select
                id="role"
                value={roleKey}
                onChange={(e) => setRoleKey(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition bg-white"
              >
                {sortedCategories.map(cat => (
                  <optgroup key={cat} label={cat}>
                    {rolesByCategory[cat].map(r => (
                      <option key={r.key} value={r.key}>{r.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* Location */}
            <div className="md:col-span-1">
              <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                Location
              </label>
              <select
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition bg-white"
              >
                {LOCATIONS.map(l => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>

            {/* Experience Level */}
            <div className="md:col-span-2">
              <label htmlFor="experience" className="block text-sm font-semibold text-gray-700 mb-2">
                Experience Level
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {EXPERIENCE_LEVELS.map(e => (
                  <button
                    key={e.value}
                    type="button"
                    onClick={() => setExperience(e.value)}
                    className={`py-2.5 px-3 rounded-xl text-sm font-semibold border-2 transition-all cursor-pointer ${
                      experience === e.value
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {e.label.split(' (')[0]}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">{experienceLabel(experience)}</p>
            </div>
          </div>

          {/* Research Button */}
          <div className="mt-8">
            <button
              onClick={handleResearch}
              className="w-full bg-gradient-to-r from-emerald-600 to-green-500 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:from-emerald-700 hover:to-green-600 transition-all text-lg cursor-pointer"
            >
              Research Salary
            </button>
          </div>
        </div>

        {/* ---------- Results ---------- */}
        {result && (
          <div className="mt-10 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

            {/* ===== Salary Headline Card ===== */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
              <p className="text-sm uppercase tracking-widest text-gray-400 mb-2">Estimated Median Base Salary</p>
              <h3 className="text-4xl sm:text-5xl font-extrabold text-emerald-700 mb-2">
                {fmt(result.adjP50)}
              </h3>
              <p className="text-gray-600 text-lg">
                {result.role.label} &middot; {experienceLabel(result.experience).split(' (')[0]} &middot; {locationLabel(result.location).split(' (')[0]}
              </p>
              <div className="mt-4 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold px-4 py-2 rounded-full">
                Range: {fmt(result.adjP10)} &ndash; {fmt(result.adjP90)}
              </div>
            </div>

            {/* ===== Salary Range Visualization ===== */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Salary Range Distribution</h3>
              <p className="text-sm text-gray-500 mb-4">
                Percentile markers show where salaries fall across the market for this role, location, and experience level.
              </p>
              <SalaryRangeBar
                p10={result.adjP10}
                p25={result.adjP25}
                p50={result.adjP50}
                p75={result.adjP75}
                p90={result.adjP90}
              />
              <div className="grid grid-cols-5 gap-2 mt-4">
                {[
                  { label: '10th Pctl', value: result.adjP10, desc: 'Low end',     bg: 'bg-red-50',    text: 'text-red-600' },
                  { label: '25th Pctl', value: result.adjP25, desc: 'Below avg',   bg: 'bg-amber-50',  text: 'text-amber-600' },
                  { label: '50th Pctl', value: result.adjP50, desc: 'Median',      bg: 'bg-green-50',  text: 'text-green-700' },
                  { label: '75th Pctl', value: result.adjP75, desc: 'Above avg',   bg: 'bg-blue-50',   text: 'text-blue-600' },
                  { label: '90th Pctl', value: result.adjP90, desc: 'Top earners', bg: 'bg-violet-50', text: 'text-violet-600' },
                ].map(item => (
                  <div key={item.label} className={`${item.bg} rounded-xl p-3 text-center`}>
                    <p className="text-[10px] font-medium text-gray-500">{item.label}</p>
                    <p className={`text-sm sm:text-base font-extrabold ${item.text}`}>{fmt(item.value)}</p>
                    <p className="text-[10px] text-gray-400">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ===== Total Compensation Breakdown ===== */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Total Compensation Breakdown</h3>
              <p className="text-sm text-gray-500 mb-6">
                Base salary is only part of the picture. Here is the typical total compensation at the median.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Base Salary',      value: result.adjP50,        color: 'text-emerald-700', bg: 'bg-emerald-50' },
                  { label: 'Bonus / Variable',  value: result.bonusAmount,   color: 'text-amber-600',   bg: 'bg-amber-50' },
                  { label: 'Equity / RSUs',     value: result.equityAmount,  color: 'text-blue-600',    bg: 'bg-blue-50' },
                  { label: 'Benefits Value',    value: result.benefitsValue, color: 'text-violet-600',  bg: 'bg-violet-50' },
                ].map(item => (
                  <div key={item.label} className={`${item.bg} rounded-xl p-4 text-center`}>
                    <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                    <p className={`text-xl sm:text-2xl font-extrabold ${item.color}`}>{fmt(item.value)}</p>
                  </div>
                ))}
              </div>

              {/* Total comp bar */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700">Estimated Total Compensation</span>
                  <span className="text-2xl font-extrabold text-gray-900">{fmt(result.totalCompMedian)}</span>
                </div>
                <div className="h-4 rounded-full overflow-hidden flex">
                  {(() => {
                    const total = result.totalCompMedian;
                    const baseW = (result.adjP50 / total) * 100;
                    const bonusW = (result.bonusAmount / total) * 100;
                    const equityW = (result.equityAmount / total) * 100;
                    const benefitsW = (result.benefitsValue / total) * 100;
                    return (
                      <>
                        <div className="h-full bg-emerald-400" style={{ width: `${baseW}%` }} title="Base" />
                        <div className="h-full bg-amber-400" style={{ width: `${bonusW}%` }} title="Bonus" />
                        <div className="h-full bg-blue-400" style={{ width: `${equityW}%` }} title="Equity" />
                        <div className="h-full bg-violet-400" style={{ width: `${benefitsW}%` }} title="Benefits" />
                      </>
                    );
                  })()}
                </div>
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs mt-2">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-400 inline-block" /> Base ({Math.round((result.adjP50 / result.totalCompMedian) * 100)}%)</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400 inline-block" /> Bonus ({Math.round((result.bonusAmount / result.totalCompMedian) * 100)}%)</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-400 inline-block" /> Equity ({Math.round((result.equityAmount / result.totalCompMedian) * 100)}%)</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-violet-400 inline-block" /> Benefits ({Math.round((result.benefitsValue / result.totalCompMedian) * 100)}%)</span>
                </div>
              </div>

              <p className="text-xs text-gray-400 mt-3">
                Bonus ({result.role.bonusPct}% of base) and equity ({result.role.equityPct}% of base, annualized) are typical for this role.
                Benefits include health insurance, 401k match, and other perks.
              </p>
            </div>

            {/* ===== Experience Level Impact ===== */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">How Experience Affects Pay</h3>
              <p className="text-sm text-gray-500 mb-6">
                Median base salary for {result.role.label} in {locationLabel(result.location).split(' (')[0]} at each experience level.
              </p>
              <div className="space-y-4">
                {result.experienceImpact.map((lvl) => {
                  const maxMedian = Math.max(...result.experienceImpact.map(l => l.median));
                  const barPct = Math.round((lvl.median / maxMedian) * 100);
                  const isCurrentLevel = lvl.level === experienceLabel(result.experience).split(' (')[0];
                  return (
                    <div key={lvl.level}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${isCurrentLevel ? 'text-emerald-700 font-bold' : 'text-gray-700'}`}>
                          {lvl.level} {isCurrentLevel && '(you)'}
                        </span>
                        <span className={`text-sm font-bold ${isCurrentLevel ? 'text-emerald-700' : 'text-gray-900'}`}>
                          {fmt(lvl.median)}
                        </span>
                      </div>
                      <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ease-out ${isCurrentLevel ? 'bg-emerald-500' : 'bg-gray-300'}`}
                          style={{ width: `${barPct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {experience !== 'executive' && (
                <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-sm text-emerald-800">
                    <span className="font-bold">Growth potential:</span>{' '}
                    Moving from {experienceLabel(result.experience).split(' (')[0]} to the next level could increase your
                    median base salary by approximately{' '}
                    <span className="font-bold">
                      {fmt(
                        result.experienceImpact[
                          EXPERIENCE_LEVELS.findIndex(e => e.value === result.experience) + 1
                        ]?.median - result.adjP50
                      )}
                    </span>{' '}
                    per year.
                  </p>
                </div>
              )}
            </div>

            {/* ===== Related Roles Comparison ===== */}
            {result.relatedRoles.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Related Roles Comparison</h3>
                <p className="text-sm text-gray-500 mb-6">
                  See how similar roles in {result.role.category} compare at the same experience and location.
                </p>
                <div className="space-y-3">
                  {/* Current role first */}
                  <div className="flex items-center gap-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-emerald-700 truncate">{result.role.label}</p>
                      <p className="text-xs text-gray-500">Current selection</p>
                    </div>
                    <p className="text-lg font-extrabold text-emerald-700 whitespace-nowrap">{fmt(result.adjP50)}</p>
                  </div>
                  {/* Related roles */}
                  {result.relatedRoles.map(({ key, role, adjP50 }) => {
                    const diff = adjP50 - result.adjP50;
                    const isHigher = diff > 0;
                    return (
                      <div key={key} className="flex items-center gap-4 bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{role.label}</p>
                          <p className="text-xs text-gray-500">{role.category}</p>
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <p className="text-lg font-extrabold text-gray-900">{fmt(adjP50)}</p>
                          <p className={`text-xs font-semibold ${isHigher ? 'text-green-600' : diff < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                            {isHigher ? '+' : ''}{fmt(diff)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ===== Negotiation Leverage Tips ===== */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Negotiation Leverage Tips</h3>
              <p className="text-sm text-gray-500 mb-6">
                Actionable strategies to maximize your compensation for this role.
              </p>
              <ul className="space-y-4">
                {result.negotiationTips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700">
                    <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </span>
                    <span className="text-sm leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* ===== Methodology ===== */}
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-3">How This Tool Works</h3>
              <div className="text-sm text-gray-600 space-y-2">
                <p>
                  We use aggregated salary data from public sources for {Object.keys(ROLES).length}+ common roles in the United States
                  (2024-2025). Base percentiles (10th, 25th, 50th, 75th, 90th) are then adjusted using two multipliers:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>
                    <span className="font-semibold text-gray-700">Location:</span> Major metros add ~18%, remote adds ~5%, and smaller markets reduce by ~13%.
                  </li>
                  <li>
                    <span className="font-semibold text-gray-700">Experience:</span> Entry-level earns ~78% of the mid-level baseline; senior earns ~118%; executive/lead earns ~135%.
                  </li>
                </ul>
                <p>
                  Total compensation estimates include typical bonus percentages, equity/RSU grants, and benefits values for each role.
                  This tool provides estimates for informational purposes only. Actual salaries vary based on company size,
                  specific skills, negotiation, equity vesting schedules, and many other factors.
                </p>
                <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <strong>Sources &amp; Further Reading:</strong>{' '}
                  <a href="https://www.bls.gov/oes/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">BLS Occupational Employment &amp; Wage Statistics</a>
                  {' · '}
                  <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics Occupational Outlook Handbook</a>
                  {' · '}
                  <a href="https://www.onetonline.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">O*NET Online - Wage &amp; Employment Trends</a>
                </p>
              </div>
            </div>

            {/* ===== CTA ===== */}
            <ToolCTA
              headline="Know your worth? Now show it."
              description="A polished, ATS-optimized resume gives you leverage in any salary conversation. Build yours in minutes with Superpower Resume."
              buttonText="Build My Resume Free"
            />
          </div>
        )}
      </div>
    </div>
  );
}
